import {
  AlertTriangle,
  BarChart3,
  CheckCircle,
  ChevronDown,
  Download,
  ExternalLink,
  FileDown,
  Globe,
  Lightbulb,
  Presentation,
  Search,
  Shield,
  Smartphone,
  TrendingUp,
  XCircle,
  Zap,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'react-toastify'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  seoTechnicalAudit,
  UrlSeoTechnicalAuditResult,
} from 'sprites-types/workflow/tools/seoTechnicalAudit'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

interface UrlAuditData extends UrlSeoTechnicalAuditResult {
  [key: string]: any
}

// Priority badge component
function PriorityBadge({ priority }: { priority: 'high' | 'medium' | 'low' }) {
  const styles = {
    high: 'bg-red-100 text-red-700 border-red-200',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    low: 'bg-green-100 text-green-700 border-green-200',
  }

  const icons = {
    high: '🔴',
    medium: '🟡',
    low: '🟢',
  }

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${styles[priority]}`}
    >
      <span>{icons[priority]}</span>
      {priority.charAt(0).toUpperCase() + priority.slice(1)}
    </span>
  )
}

// Category badge component
function CategoryBadge({ category }: { category: string }) {
  const styles: Record<string, string> = {
    Performance: 'bg-blue-100 text-blue-700 border-blue-200',
    SEO: 'bg-purple-100 text-purple-700 border-purple-200',
    Accessibility: 'bg-orange-100 text-orange-700 border-orange-200',
    'Best Practices': 'bg-pink-100 text-pink-700 border-pink-200',
    Technical: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  }

  const style = styles[category] || 'bg-gray-100 text-gray-700 border-gray-200'

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${style}`}
    >
      {category}
    </span>
  )
}

function UrlAuditItem({ audit, className }: { audit: UrlAuditData; className?: string }) {
  const url = audit.source_url || 'Unknown URL'
  const mobileSpeed = audit.mobile_pagespeed_score || 0
  const desktopSpeed = audit.desktop_pagespeed_score || 0
  const mobileOpt = audit.mobile_optimized_score || 0
  const desktopOpt = audit.desktop_optimized_score || 0
  const title = audit.document_title || 'No title'

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-100'
    if (score >= 60) return 'text-yellow-600 bg-yellow-100'
    return 'text-red-600 bg-red-100'
  }

  const getTestStatus = (test: string | undefined) => {
    if (!test || test.includes('No data') || test === 'Unknown') {
      return { icon: XCircle, color: 'text-red-500', status: 'Failed' }
    }
    if (
      test.toLowerCase().includes('valid') ||
      test.toLowerCase().includes('successful') ||
      test.toLowerCase().includes('have')
    ) {
      return { icon: CheckCircle, color: 'text-green-500', status: 'Passed' }
    }
    return { icon: AlertTriangle, color: 'text-yellow-500', status: 'Warning' }
  }

  const seoTests = [
    { name: 'Title Element', value: audit.document_title },
    { name: 'Meta Description', value: audit.meta_description },
    { name: 'Canonical URL', value: audit.canonical },
    { name: 'HTTP Status', value: audit.http_status_code },
    { name: 'Robots.txt', value: audit.robots_txt },
    { name: 'Crawlable', value: audit.is_crawlable },
    { name: 'Crawlable Anchors', value: audit.crawlable_anchors },
    { name: 'Link Text', value: audit.link_text },
    { name: 'Image Alt', value: audit.image_alt },
    { name: 'Hreflang', value: audit.hreflang },
  ]

  return (
    <div
      className={`rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${className}`}
    >
      <div className="px-4 py-3">
        <div className="mb-3 flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-gray-900">{url}</div>
            <div className="truncate text-xs text-gray-600">{title}</div>
            {url !== 'Unknown URL' && (
              <a
                href={url.startsWith('http') ? url : `https://${url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 flex items-center gap-1 text-xs text-blue-600 hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                Visit
              </a>
            )}
          </div>
        </div>

        <div className="mb-3 grid grid-cols-2 gap-4">
          <div>
            <div className="mb-1 text-xs text-gray-600">Mobile Scores</div>
            <div className="flex gap-2">
              <div
                className={`rounded px-2 py-1 text-xs font-medium ${getScoreColor(mobileSpeed)}`}
              >
                Speed: {mobileSpeed}
              </div>
              <div className={`rounded px-2 py-1 text-xs font-medium ${getScoreColor(mobileOpt)}`}>
                Opt: {mobileOpt}
              </div>
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs text-gray-600">Desktop Scores</div>
            <div className="flex gap-2">
              <div
                className={`rounded px-2 py-1 text-xs font-medium ${getScoreColor(desktopSpeed)}`}
              >
                Speed: {desktopSpeed}
              </div>
              <div className={`rounded px-2 py-1 text-xs font-medium ${getScoreColor(desktopOpt)}`}>
                Opt: {desktopOpt}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-1">
          <div className="text-xs font-medium text-gray-700">SEO Tests</div>
          <div className="grid grid-cols-2 gap-1 text-xs">
            {seoTests.map((test, index) => {
              const testResult = getTestStatus(test.value)
              const TestIcon = testResult.icon
              return (
                <div key={index} className="flex items-center gap-1">
                  <TestIcon className={`h-3 w-3 ${testResult.color}`} />
                  <span className="text-gray-700">{test.name}</span>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

function SeoTechnicalAuditDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const [expandedAudit, setExpandedAudit] = useState<string | null>(null)
  const [passedChecksExpanded, setPassedChecksExpanded] = useState(false)
  const [expandedInsights, setExpandedInsights] = useState<Record<string, boolean>>({})
  const [isExportingPdf, setIsExportingPdf] = useState(false)
  const [isExpanded, setIsExpanded] = useState(true)
  const schema = getToolResultSchema(seoTechnicalAudit, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const toggleInsight = (index: string) => {
    setExpandedInsights((prev) => ({ ...prev, [index]: !prev[index] }))
  }

  const handleExportToPdf = async () => {
    setIsExportingPdf(true)

    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/services/seo-pdf-export`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            seoData: parseResult.data,
            filename: `SEO_Technical_Audit_${new Date().toISOString().split('T')[0]}`,
          }),
        }
      )

      if (!response.ok) {
        throw new Error('Failed to export PDF')
      }

      const { url } = await response.json()

      if (url) {
        // Open PDF in new tab instead of downloading
        window.open(url, '_blank')

        toast.success('🎉 PDF report opened in new tab!')
      } else {
        throw new Error('No download URL received')
      }
    } catch (error) {
      console.error('PDF export failed:', error)
      toast.error('Failed to export PDF. Please try again.')
    } finally {
      setIsExportingPdf(false)
    }
  }

  // Create context menu actions
  const contextActions: ContextAction[] = [
    {
      id: 'export-pdf',
      label: isExportingPdf ? 'Exporting...' : 'Export to PDF Report',
      icon: isExportingPdf ? (
        <Download className="h-4 w-4 animate-pulse" />
      ) : (
        <Presentation className="h-4 w-4" />
      ),
      onClick: handleExportToPdf,
      disabled: isExportingPdf,
    },
  ]

  const result = parseResult.data
  const action = result.action
  const resultData = result.result

  // Enhanced categorization logic
  const categorizeIssues = (data: UrlSeoTechnicalAuditResult) => {
    const highPriority: Array<{
      category: string
      finding: string
      recommendation: string
      impact: string
      priority: 'high' | 'medium' | 'low'
    }> = []
    const mediumPriority: Array<{
      category: string
      finding: string
      recommendation: string
      impact: string
      priority: 'high' | 'medium' | 'low'
    }> = []
    const lowPriority: Array<{
      category: string
      finding: string
      recommendation: string
      impact: string
      priority: 'high' | 'medium' | 'low'
    }> = []
    const successes: string[] = []

    // Performance issues (High Priority)
    if ((data.mobile_pagespeed_score || 0) < 50) {
      highPriority.push({
        category: 'Performance',
        finding: `Mobile PageSpeed score is critically low at ${data.mobile_pagespeed_score || 0}/100`,
        recommendation:
          'Optimize images, enable compression, minimize JavaScript and CSS, and leverage browser caching',
        impact: 'Poor mobile performance directly impacts user experience and search rankings',
        priority: 'high',
      })
    }
    if ((data.desktop_pagespeed_score || 0) < 50) {
      highPriority.push({
        category: 'Performance',
        finding: `Desktop PageSpeed score is critically low at ${data.desktop_pagespeed_score || 0}/100`,
        recommendation:
          'Optimize server response time, compress assets, and minimize render-blocking resources',
        impact: 'Slow desktop performance affects user engagement and conversion rates',
        priority: 'high',
      })
    }

    // SEO Critical Issues (High Priority)
    if (
      !data.document_title ||
      data.document_title.includes('No data') ||
      data.document_title === 'Unknown'
    ) {
      highPriority.push({
        category: 'SEO',
        finding: 'Missing or invalid page title',
        recommendation: 'Add a descriptive, keyword-rich title tag (50-60 characters)',
        impact: 'Title tags are crucial for search engine rankings and click-through rates',
        priority: 'high',
      })
    }
    if (
      !data.meta_description ||
      data.meta_description.includes('No data') ||
      data.meta_description === 'Unknown'
    ) {
      highPriority.push({
        category: 'SEO',
        finding: 'Missing or invalid meta description',
        recommendation:
          'Add a compelling meta description (150-160 characters) that summarizes the page content',
        impact: 'Meta descriptions directly influence search result click-through rates',
        priority: 'high',
      })
    }

    // Medium Priority Issues
    if ((data.mobile_pagespeed_score || 0) >= 50 && (data.mobile_pagespeed_score || 0) < 80) {
      mediumPriority.push({
        category: 'Performance',
        finding: `Mobile PageSpeed score needs improvement: ${data.mobile_pagespeed_score || 0}/100`,
        recommendation:
          'Focus on Core Web Vitals optimization, image compression, and reducing JavaScript bundle size',
        impact:
          'Moderate performance improvements will enhance user experience and search rankings',
        priority: 'medium',
      })
    }
    if (!data.canonical || data.canonical.includes('No data')) {
      mediumPriority.push({
        category: 'SEO',
        finding: 'Missing canonical URL',
        recommendation: 'Add canonical tags to prevent duplicate content issues',
        impact: 'Helps search engines understand the preferred version of your content',
        priority: 'medium',
      })
    }

    // Low Priority Issues
    if (data.image_alt && data.image_alt.includes('attention')) {
      lowPriority.push({
        category: 'Accessibility',
        finding: 'Some images missing alt text',
        recommendation: 'Add descriptive alt text to all images for better accessibility and SEO',
        impact: 'Improves accessibility and provides additional SEO context',
        priority: 'low',
      })
    }

    // Success items
    if (
      data.document_title &&
      !data.document_title.includes('No data') &&
      data.document_title !== 'Unknown'
    ) {
      successes.push('Document Title: Valid')
    }
    if (
      data.meta_description &&
      !data.meta_description.includes('No data') &&
      data.meta_description !== 'Unknown'
    ) {
      successes.push('Meta Description: Present')
    }
    if ((data.mobile_pagespeed_score || 0) >= 80) {
      successes.push('Mobile Performance: Excellent')
    }
    if ((data.desktop_pagespeed_score || 0) >= 80) {
      successes.push('Desktop Performance: Excellent')
    }
    if (data.canonical && !data.canonical.includes('No data')) {
      successes.push('Canonical URL: Properly set')
    }
    if (data.is_crawlable && data.is_crawlable.includes('accessible')) {
      successes.push('Crawlability: Accessible to search engines')
    }

    return { highPriority, mediumPriority, lowPriority, successes }
  }

  if (action === 'AuditURL') {
    // Single URL audit - enhanced version
    const data = resultData as UrlSeoTechnicalAuditResult
    const url = data?.source_url || 'Unknown URL'
    const { highPriority, mediumPriority, lowPriority, successes } = categorizeIssues(data)
    const allInsights = [...highPriority, ...mediumPriority, ...lowPriority]

    // Calculate enhanced score
    const mobileScore = data?.mobile_pagespeed_score || 0
    const desktopScore = data?.desktop_pagespeed_score || 0
    const avgPageSpeed = Math.round((mobileScore + desktopScore) / 2)

    // Factor in SEO completeness for overall score
    let seoBonus = 0
    if (data?.document_title && !data.document_title.includes('No data')) seoBonus += 5
    if (data?.meta_description && !data.meta_description.includes('No data')) seoBonus += 5
    if (data?.canonical && !data.canonical.includes('No data')) seoBonus += 3
    if (data?.is_crawlable && data.is_crawlable.includes('accessible')) seoBonus += 2

    const score = Math.min(100, avgPageSpeed + seoBonus)

    const getScoreColor = (score: number) => {
      if (score >= 80) return 'text-green-600'
      if (score >= 60) return 'text-yellow-600'
      return 'text-red-600'
    }

    const getScoreBg = (score: number) => {
      if (score >= 80) return 'bg-green-100'
      if (score >= 60) return 'bg-yellow-100'
      return 'bg-red-100'
    }

    // Generate executive summary
    const generateExecutiveSummary = () => {
      const criticalIssues = highPriority.length
      const totalIssues = allInsights.length
      const performanceStatus =
        score >= 80 ? 'excellent' : score >= 60 ? 'good' : 'needs improvement'

      return (
        `**SEO Technical Audit Summary for ${url}**

Overall performance score: **${score}/100** (${performanceStatus}). ` +
        `${criticalIssues > 0 ? `Found ${criticalIssues} high-priority issues that require immediate attention. ` : ''}` +
        `${totalIssues > 0 ? `Total of ${totalIssues} optimization opportunities identified. ` : 'No major issues detected. '}` +
        `Mobile PageSpeed: ${mobileScore}/100, Desktop PageSpeed: ${desktopScore}/100.`
      )
    }

    return (
      <div className="space-y-3">
        {/* Header */}
        <ContextMenuWrapper actions={contextActions}>
          <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="group flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
              aria-expanded={isExpanded}
            >
              <div className="flex-shrink-0">
                <div className={`rounded-full p-2 ${getScoreBg(score)}`}>
                  <Search className={`h-5 w-5 ${getScoreColor(score)}`} />
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex items-center gap-2">
                  <Globe className="h-4 w-4 text-blue-500" />
                  <div className="text-sm font-semibold text-gray-900">
                    SEO Technical Audit - URL
                  </div>
                </div>
                <div className="text-xs font-medium tracking-wide text-gray-500">
                  {allInsights.length} insights • {highPriority.length} high priority
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleExportToPdf()
                  }}
                  disabled={isExportingPdf}
                  className="group/export flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 px-3 py-1.5 text-xs font-semibold text-white shadow-md transition-all duration-200 hover:shadow-lg hover:shadow-blue-200 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isExportingPdf ? (
                    <>
                      <Download className="h-3.5 w-3.5 animate-pulse" />
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <FileDown className="h-3.5 w-3.5 group-hover/export:animate-bounce" />
                      <span>Export PDF Report</span>
                    </>
                  )}
                </button>
                <div className="flex-shrink-0">
                  <div className={`text-2xl font-bold ${getScoreColor(score)}`}>{score}</div>
                  <div className="text-xs text-gray-500">Score</div>
                </div>
                <ChevronDown
                  className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                    isExpanded ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </div>
            </button>

            {isExpanded && (
              <div className="border-t border-gray-200/50 px-4 py-4">
                {/* Executive Summary */}
                <div className="mb-6 overflow-hidden rounded-xl border border-blue-200/60 bg-white shadow-sm">
                  <div className="border-b border-blue-100 bg-gradient-to-r from-blue-50 to-blue-50/50 px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500">
                        <Lightbulb className="h-4.5 w-4.5 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-bold text-gray-900">Executive Summary</div>
                        <div className="text-xs text-gray-600">
                          Overview of your SEO technical performance
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="px-5 py-5">
                    <div className="mb-4 text-sm leading-relaxed text-gray-800">
                      {generateExecutiveSummary()}
                    </div>

                    {/* Performance Metrics Grid */}
                    <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <div className="mb-1 flex items-center gap-2">
                          <Smartphone className="h-4 w-4 text-blue-600" />
                          <span className="text-xs font-medium text-gray-700">Mobile Speed</span>
                        </div>
                        <div className={`text-lg font-bold ${getScoreColor(mobileScore)}`}>
                          {mobileScore}
                        </div>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <div className="mb-1 flex items-center gap-2">
                          <BarChart3 className="h-4 w-4 text-purple-600" />
                          <span className="text-xs font-medium text-gray-700">Desktop Speed</span>
                        </div>
                        <div className={`text-lg font-bold ${getScoreColor(desktopScore)}`}>
                          {desktopScore}
                        </div>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <div className="mb-1 flex items-center gap-2">
                          <Shield className="h-4 w-4 text-green-600" />
                          <span className="text-xs font-medium text-gray-700">SEO Health</span>
                        </div>
                        <div className="text-lg font-bold text-green-600">
                          {Math.round(
                            (successes.length / (successes.length + allInsights.length || 1)) * 100
                          )}
                          %
                        </div>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <div className="mb-1 flex items-center gap-2">
                          <Zap className="h-4 w-4 text-orange-600" />
                          <span className="text-xs font-medium text-gray-700">Issues Found</span>
                        </div>
                        <div className="text-lg font-bold text-orange-600">
                          {allInsights.length}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="border-t border-gray-100 bg-gray-50/50 px-5 py-3">
                    <div className="flex flex-wrap gap-2">
                      <div className="flex items-center gap-1.5 rounded-md bg-white px-3 py-2 shadow-sm ring-1 ring-gray-200/50">
                        <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                        <span className="text-xs font-medium text-gray-700">
                          {successes.length} Passed
                        </span>
                      </div>
                      {highPriority.length > 0 && (
                        <div className="flex items-center gap-1.5 rounded-md bg-white px-3 py-2 shadow-sm ring-1 ring-red-200/50">
                          <XCircle className="h-3.5 w-3.5 text-red-600" />
                          <span className="text-xs font-medium text-gray-700">
                            {highPriority.length} High Priority
                          </span>
                        </div>
                      )}
                      {mediumPriority.length > 0 && (
                        <div className="flex items-center gap-1.5 rounded-md bg-white px-3 py-2 shadow-sm ring-1 ring-yellow-200/50">
                          <AlertTriangle className="h-3.5 w-3.5 text-yellow-600" />
                          <span className="text-xs font-medium text-gray-700">
                            {mediumPriority.length} Medium Priority
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Prioritized Issues */}
                {allInsights.length > 0 && (
                  <div>
                    <div className="mb-4 flex items-center gap-2">
                      <div className="text-sm font-bold text-gray-900">Recommended Actions</div>
                      <div className="h-px flex-1 bg-gray-200"></div>
                    </div>

                    {/* High Priority Issues */}
                    {highPriority.length > 0 && (
                      <div className="mb-5">
                        <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                          🔴 High Priority
                        </div>
                        <div className="space-y-3">
                          {highPriority.map((insight, index) => (
                            <SeoInsightCard
                              key={`high-${index}`}
                              insight={insight}
                              isExpanded={expandedInsights[`high-${index}`] || false}
                              onToggle={() => toggleInsight(`high-${index}`)}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Medium Priority Issues */}
                    {mediumPriority.length > 0 && (
                      <div className="mb-5">
                        <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                          🟡 Medium Priority
                        </div>
                        <div className="space-y-3">
                          {mediumPriority.map((insight, index) => (
                            <SeoInsightCard
                              key={`medium-${index}`}
                              insight={insight}
                              isExpanded={expandedInsights[`medium-${index}`] || false}
                              onToggle={() => toggleInsight(`medium-${index}`)}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Low Priority Issues */}
                    {lowPriority.length > 0 && (
                      <div className="mb-3">
                        <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                          🟢 Low Priority
                        </div>
                        <div className="space-y-3">
                          {lowPriority.map((insight, index) => (
                            <SeoInsightCard
                              key={`low-${index}`}
                              insight={insight}
                              isExpanded={expandedInsights[`low-${index}`] || false}
                              onToggle={() => toggleInsight(`low-${index}`)}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Passed Checks */}
                {successes.length > 0 && (
                  <div>
                    <button
                      onClick={() => setPassedChecksExpanded(!passedChecksExpanded)}
                      className="mb-2 flex w-full items-center justify-between gap-1 text-left"
                    >
                      <div className="flex items-center gap-1 text-sm font-semibold text-green-600">
                        <CheckCircle className="h-4 w-4" />
                        Passed Checks
                      </div>
                      <ChevronDown
                        className={`h-4 w-4 text-green-500 transition-transform duration-200 ${
                          passedChecksExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    <div className="text-xs text-green-700">
                      {successes.length} checks passed successfully
                    </div>
                    {passedChecksExpanded && (
                      <div className="mt-2 space-y-1">
                        {successes.map((success: string, index: number) => (
                          <div
                            key={index}
                            className="rounded bg-green-50 px-2 py-1 text-xs text-green-800"
                          >
                            <div className="flex items-center gap-1">
                              <CheckCircle className="h-3 w-3 text-green-600" />
                              {success}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </ContextMenuWrapper>
      </div>
    )
  } else {
    // Site audit - multiple URLs
    const siteData = resultData as {
      domain_created_date?: string
      url_audits: UrlSeoTechnicalAuditResult[]
    }
    const audits = siteData.url_audits || []
    const domainDate = siteData.domain_created_date

    // Calculate overall stats
    const totalAudits = audits.length
    const avgMobileSpeed = Math.round(
      audits.reduce((sum, audit) => sum + (audit.mobile_pagespeed_score || 0), 0) / totalAudits
    )
    const avgDesktopSpeed = Math.round(
      audits.reduce((sum, audit) => sum + (audit.desktop_pagespeed_score || 0), 0) / totalAudits
    )
    const overallScore = Math.round((avgMobileSpeed + avgDesktopSpeed) / 2)

    const getScoreColor = (score: number) => {
      if (score >= 80) return 'text-green-600'
      if (score >= 60) return 'text-yellow-600'
      return 'text-red-600'
    }

    const getScoreBg = (score: number) => {
      if (score >= 80) return 'bg-green-100'
      if (score >= 60) return 'bg-yellow-100'
      return 'bg-red-100'
    }

    return (
      <div className="space-y-3">
        <ContextMenuWrapper actions={contextActions}>
          <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
            {/* Header */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="group flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
              aria-expanded={isExpanded}
            >
              <div className="flex-shrink-0">
                <div className={`rounded-full p-2 ${getScoreBg(overallScore)}`}>
                  <Search className={`h-5 w-5 ${getScoreColor(overallScore)}`} />
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex items-center gap-2">
                  <Globe className="h-4 w-4 text-blue-500" />
                  <div className="text-sm font-semibold text-gray-900">
                    SEO Technical Audit - Site
                  </div>
                </div>
                <div className="text-xs font-medium tracking-wide text-gray-500">
                  {totalAudits} pages analyzed{domainDate && ` • Domain created: ${domainDate}`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleExportToPdf()
                  }}
                  disabled={isExportingPdf}
                  className="group/export flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 px-3 py-1.5 text-xs font-semibold text-white shadow-md transition-all duration-200 hover:shadow-lg hover:shadow-blue-200 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isExportingPdf ? (
                    <>
                      <Download className="h-3.5 w-3.5 animate-pulse" />
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <FileDown className="h-3.5 w-3.5 group-hover/export:animate-bounce" />
                      <span>Export PDF Report</span>
                    </>
                  )}
                </button>
                <div className="flex-shrink-0">
                  <div className={`text-2xl font-bold ${getScoreColor(overallScore)}`}>
                    {overallScore}
                  </div>
                  <div className="text-xs text-gray-500">Score</div>
                </div>
                <ChevronDown
                  className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                    isExpanded ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </div>
            </button>

            {isExpanded && (
              <div className="border-t border-gray-200/50 px-4 py-4">
                {/* Site Performance Summary */}
                <div className="mb-6">
                  <div className="mb-4 flex items-center gap-2">
                    <div className="text-sm font-bold text-gray-900">Site Performance Overview</div>
                    <div className="h-px flex-1 bg-gray-200"></div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="mb-1 flex items-center gap-2">
                        <Globe className="h-4 w-4 text-blue-600" />
                        <span className="text-xs font-medium text-gray-700">Pages Audited</span>
                      </div>
                      <div className="text-lg font-bold text-blue-600">{totalAudits}</div>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="mb-1 flex items-center gap-2">
                        <Smartphone className="h-4 w-4 text-green-600" />
                        <span className="text-xs font-medium text-gray-700">Avg Mobile</span>
                      </div>
                      <div className={`text-lg font-bold ${getScoreColor(avgMobileSpeed)}`}>
                        {avgMobileSpeed}
                      </div>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="mb-1 flex items-center gap-2">
                        <BarChart3 className="h-4 w-4 text-purple-600" />
                        <span className="text-xs font-medium text-gray-700">Avg Desktop</span>
                      </div>
                      <div className={`text-lg font-bold ${getScoreColor(avgDesktopSpeed)}`}>
                        {avgDesktopSpeed}
                      </div>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="mb-1 flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-orange-600" />
                        <span className="text-xs font-medium text-gray-700">Overall Score</span>
                      </div>
                      <div className={`text-lg font-bold ${getScoreColor(overallScore)}`}>
                        {overallScore}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Audit Results */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-gray-900">Page Results</div>
                    <div className="text-xs text-gray-500">{audits.length} pages</div>
                  </div>

                  {audits.slice(0, 10).map((audit, index) => {
                    const auditKey = audit.source_url || `audit-${index}`
                    const isExpanded = expandedAudit === auditKey

                    return (
                      <div key={index} className="space-y-2">
                        {/* Compact view */}
                        <div
                          className="flex cursor-pointer items-center justify-between rounded-lg border border-gray-200 bg-white p-3 hover:bg-gray-50"
                          onClick={() => setExpandedAudit(isExpanded ? null : auditKey)}
                        >
                          <div className="flex-1">
                            <div className="truncate text-sm font-medium text-gray-900">
                              {audit.source_url || `URL ${index + 1}`}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-600">
                              <span>Mobile: {audit.mobile_pagespeed_score || 0}</span>
                              <span>•</span>
                              <span>Desktop: {audit.desktop_pagespeed_score || 0}</span>
                            </div>
                          </div>
                          <ChevronDown
                            className={`h-4 w-4 text-gray-400 transition-transform ${
                              isExpanded ? 'rotate-180' : ''
                            }`}
                          />
                        </div>

                        {/* Expanded view */}
                        {isExpanded && <UrlAuditItem audit={audit} className="ml-4" />}
                      </div>
                    )
                  })}

                  {audits.length > 10 && (
                    <div className="text-center text-xs text-gray-500">
                      +{audits.length - 10} more pages (click individual results to expand)
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </ContextMenuWrapper>
      </div>
    )
  }
}

// SEO Insight Card Component - Shows individual SEO recommendations
function SeoInsightCard({
  insight,
  isExpanded,
  onToggle,
}: {
  insight: {
    category: string
    finding: string
    recommendation: string
    impact: string
    priority: 'high' | 'medium' | 'low'
  }
  isExpanded: boolean
  onToggle: () => void
}) {
  // Priority-based styling
  const priorityStyles = {
    high: {
      border: 'border-red-300',
      shadow: 'shadow-md shadow-red-100/50',
      accent: 'border-l-4 border-l-red-500',
      bg: 'bg-red-50/30',
      hoverBg: 'hover:bg-red-50/50',
      icon: 'text-red-600',
      iconBg: 'bg-red-100',
      expandedBg: 'bg-red-50/40',
      expandedBorder: 'border-red-200',
    },
    medium: {
      border: 'border-yellow-300',
      shadow: 'shadow-md shadow-yellow-100/50',
      accent: 'border-l-4 border-l-yellow-500',
      bg: 'bg-yellow-50/30',
      hoverBg: 'hover:bg-yellow-50/50',
      icon: 'text-yellow-600',
      iconBg: 'bg-yellow-100',
      expandedBg: 'bg-yellow-50/40',
      expandedBorder: 'border-yellow-200',
    },
    low: {
      border: 'border-green-300',
      shadow: 'shadow shadow-green-100/50',
      accent: 'border-l-4 border-l-green-500',
      bg: 'bg-green-50/20',
      hoverBg: 'hover:bg-green-50/40',
      icon: 'text-green-600',
      iconBg: 'bg-green-100',
      expandedBg: 'bg-green-50/30',
      expandedBorder: 'border-green-200',
    },
  }

  const styles = priorityStyles[insight.priority]

  return (
    <div
      className={`overflow-hidden rounded-2xl ${styles.border} ${styles.shadow} ${styles.accent} ${styles.bg} transition-all duration-200`}
    >
      <button
        onClick={onToggle}
        className={`flex w-full items-start gap-3 p-4 text-left transition-colors ${styles.hoverBg}`}
      >
        <div className="flex-shrink-0 pt-0.5">
          <div className={`rounded-lg p-1.5 ${styles.iconBg}`}>
            {insight.priority === 'high' ? (
              <XCircle className={`h-4.5 w-4.5 ${styles.icon}`} />
            ) : insight.priority === 'medium' ? (
              <AlertTriangle className={`h-4.5 w-4.5 ${styles.icon}`} />
            ) : (
              <CheckCircle className={`h-4.5 w-4.5 ${styles.icon}`} />
            )}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center gap-2">
            <CategoryBadge category={insight.category} />
            <PriorityBadge priority={insight.priority} />
          </div>
          <div className="text-sm font-semibold leading-relaxed text-gray-900">
            {insight.finding}
          </div>
        </div>
        <div className="flex-shrink-0">
          <ChevronDown
            className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
              isExpanded ? 'rotate-180' : ''
            }`}
          />
        </div>
      </button>

      {isExpanded && (
        <div className={`border-t ${styles.expandedBorder} ${styles.expandedBg} px-4 py-4`}>
          <div className="mb-3 rounded-lg border border-blue-200 bg-white/80 p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-1.5">
              <div className="rounded bg-blue-100 px-2 py-0.5">
                <span className="text-xs font-bold text-blue-700">💡 ACTION</span>
              </div>
            </div>
            <div className="text-sm font-medium leading-relaxed text-gray-800">
              {insight.recommendation}
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white/60 p-3">
            <div className="mb-1.5 text-xs font-semibold text-gray-600">📈 Expected Impact</div>
            <div className="text-sm leading-relaxed text-gray-700">{insight.impact}</div>
          </div>
        </div>
      )}
    </div>
  )
}

function downloadSeoTechnicalAudit(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function seoTechnicalAuditToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(seoTechnicalAudit, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const action = result.action
  const resultData = result.result

  if (action === 'AuditURL') {
    // Single URL audit markdown
    const data = resultData as UrlSeoTechnicalAuditResult
    const url = data?.source_url || 'Unknown URL'
    const score =
      Math.round(
        ((data?.mobile_pagespeed_score || 0) + (data?.desktop_pagespeed_score || 0)) / 2
      ) || 0

    const issues: string[] = []
    const warnings: string[] = []
    const successes: string[] = []

    const checkField = (field: string, value: string, label: string) => {
      if (!value || value === 'No data available' || value.toLowerCase().includes('error')) {
        issues.push(`${label}: ${value || 'Missing'}`)
      } else if (
        value.toLowerCase().includes('warning') ||
        value.toLowerCase().includes('attention')
      ) {
        warnings.push(`${label}: ${value}`)
      } else {
        successes.push(`${label}: Valid`)
      }
    }

    checkField('link_text', data?.link_text, 'Link Text')
    checkField('hreflang', data?.hreflang, 'Hreflang')
    checkField('image_alt', data?.image_alt, 'Image Alt Text')
    checkField('robots_txt', data?.robots_txt, 'Robots.txt')
    checkField('crawlable_anchors', data?.crawlable_anchors, 'Crawlable Anchors')
    checkField('is_crawlable', data?.is_crawlable, 'Crawlability')
    checkField('canonical', data?.canonical, 'Canonical URL')
    checkField('meta_description', data?.meta_description, 'Meta Description')
    checkField('http_status_code', data?.http_status_code, 'HTTP Status')
    checkField('document_title', data?.document_title, 'Document Title')

    if (data?.mobile_pagespeed_score < 60) {
      warnings.push(`Mobile PageSpeed: Low score (${data?.mobile_pagespeed_score})`)
    }
    if (data?.desktop_pagespeed_score < 60) {
      warnings.push(`Desktop PageSpeed: Low score (${data?.desktop_pagespeed_score})`)
    }

    let markdown = `## SEO Technical Audit - URL

**URL:** ${url}
**Score:** ${score}/100

### Summary
- ✅ ${successes.length} checks passed
- ⚠️ ${warnings.length} warnings
- ❌ ${issues.length} issues

`

    if (issues.length > 0) {
      markdown += `### Issues
${issues.map((issue: any) => `- ${typeof issue === 'string' ? issue : issue.message || issue.description}`).join('\n')}

`
    }

    if (warnings.length > 0) {
      markdown += `### Warnings
${warnings.map((warning: any) => `- ${typeof warning === 'string' ? warning : warning.message || warning.description}`).join('\n')}

`
    }

    return markdown + '---\n'
  } else {
    // Site audit markdown
    const siteData = resultData as {
      domain_created_date?: string
      url_audits: UrlSeoTechnicalAuditResult[]
    }
    const audits = siteData.url_audits || []
    const domainDate = siteData.domain_created_date

    const totalAudits = audits.length
    const avgMobileSpeed = Math.round(
      audits.reduce((sum, audit) => sum + (audit.mobile_pagespeed_score || 0), 0) / totalAudits
    )
    const avgDesktopSpeed = Math.round(
      audits.reduce((sum, audit) => sum + (audit.desktop_pagespeed_score || 0), 0) / totalAudits
    )
    const overallScore = Math.round((avgMobileSpeed + avgDesktopSpeed) / 2)

    let markdown = `## SEO Technical Audit - Site

**Pages Audited:** ${totalAudits}
**Overall Score:** ${overallScore}/100
**Average Mobile Speed:** ${avgMobileSpeed}
**Average Desktop Speed:** ${avgDesktopSpeed}
${domainDate ? `**Domain Created:** ${domainDate}` : ''}

### Page Results

| URL | Mobile Speed | Desktop Speed | Status |
|-----|--------------|---------------|--------|
`

    audits.forEach((audit) => {
      const url = audit.source_url || 'Unknown'
      const mobileSpeed = audit.mobile_pagespeed_score || 0
      const desktopSpeed = audit.desktop_pagespeed_score || 0
      const avgScore = Math.round((mobileSpeed + desktopSpeed) / 2)
      const status = avgScore >= 80 ? '✅' : avgScore >= 60 ? '⚠️' : '❌'

      markdown += `| ${url} | ${mobileSpeed} | ${desktopSpeed} | ${status} |\n`
    })

    return markdown + '\n---\n'
  }
}

export const SeoTechnicalAuditDisplay = createWorkflowToolComponent(
  SeoTechnicalAuditDisplayComponent,
  downloadSeoTechnicalAudit,
  seoTechnicalAuditToMarkdown
)
